import { useEffect, useMemo } from "react";
import type { WidgetTemplateDetail } from "@/modules/widget/types";

const loadedFontKeys = new Set<string>();

const DEFAULT_WEIGHTS = [400];

export function loadGoogleFont(fontName: string, weights: number[]) {
	const weightStr = weights.join(";");
	const cacheKey = `${fontName}::${weightStr}`;
	if (loadedFontKeys.has(cacheKey)) return;

	const slug = fontName.replace(/\s+/g, "-").toLowerCase();
	const linkId = `google-font-widget-${slug}-${weightStr}`;
	if (document.getElementById(linkId)) {
		loadedFontKeys.add(cacheKey);
		return;
	}

	// Remove any existing links for this font to avoid conflicting @font-face declarations
	const previewLink = document.getElementById(`google-font-preview-${slug}`);
	if (previewLink) previewLink.remove();
	document.querySelectorAll(`link[id^="google-font-widget-${slug}-"]`).forEach((el) => {
		if (el.id !== linkId) el.remove();
	});

	const link = document.createElement("link");
	link.id = linkId;
	link.rel = "stylesheet";
	link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@${weightStr}&display=swap`;
	document.head.appendChild(link);
	loadedFontKeys.add(cacheKey);
}

export function resolveWeights(layoutConfig: Record<string, unknown>, templateDetail: WidgetTemplateDetail | null): number[] {
	const raw = layoutConfig.font_body_weights ?? templateDetail?.font_body_weights;
	if (Array.isArray(raw) && raw.length > 0) {
		const nums = raw.filter((w): w is number => typeof w === "number" && w > 0);
		if (nums.length > 0) return nums;
	}
	return DEFAULT_WEIGHTS;
}

export function useWidgetFont(layoutConfig: Record<string, unknown>, templateDetail?: WidgetTemplateDetail | null): React.CSSProperties | undefined {
	const customFont = layoutConfig.custom_font === true;
	const fontBody = typeof layoutConfig.font_body === "string" ? layoutConfig.font_body : "";
	const weights = resolveWeights(layoutConfig, templateDetail ?? null);
	const weightsKey = weights.join(",");

	useEffect(() => {
		if (customFont && fontBody) {
			loadGoogleFont(fontBody, weights);
		}
	}, [customFont, fontBody, weightsKey]); // eslint-disable-line react-hooks/exhaustive-deps

	return useMemo(() => {
		if (!customFont || !fontBody) return undefined;
		return { "--sau-font-body": `"${fontBody}", sans-serif`, fontFamily: `"${fontBody}", sans-serif` } as React.CSSProperties;
	}, [customFont, fontBody]);
}
